<template>
  <div class="predicted-transaction-container" :class="{ selected: isSelected && canEditEnvelope }">
    <div @click="canEditEnvelope && toggleActions()">
      <predicted-transaction-row :predicted-transaction="predictedTransaction" :can-edit-envelope="canEditEnvelope"
                                 :out-of-date="outOfDate" :category-name="categoryName" :isSelected="isSelected" />
    </div>
    <collapse-transition>
      <actions :actions="actions" :category-name="categoryName" v-show="isSelected && canEditEnvelope" />
    </collapse-transition>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
// eslint-disable-next-line max-len
import ChangeTransactionCategoryPopup from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/cashflow-view/current-month/components/cashflow-edit/edit-transaction/ChangeTransactionCategoryPopup';
import BaseUI from 'base-ui';
import transactionUtils from '../../../../../../../../../utils/transaction';
import PredictedTransactionRow from './PredictedTransactionRow.vue';
import Actions from '../../../../../../../../../base-components/Actions.vue';
import Segment from '../../../../../../../../../Segment';
import EditPredictedWithActualFlow
  from '../../../current-month/components/cashflow-edit/edit-transaction/edit-predicted-transaction/flows/EditPredictedWithActualFlow.vue';
import EditPredictedWOActualFlow
  from '../../../current-month/components/cashflow-edit/edit-transaction/edit-predicted-transaction/flows/EditPredictedWOActualFlow.vue';
import EditEmptyEnvelopeFlow
  from '../../../current-month/components/cashflow-edit/edit-transaction/edit-predicted-transaction/flows/EditEmptyEnvelopeFlow.vue';

export default {
  name: 'PredictedTransaction',
  props: {
    predictedTransaction: {
      type: Object,
      required: true,
    },
    canEditCategory: {
      type: Boolean,
      required: true,
    },
    categoryName: {
      type: String,
      required: false,
    },
    categoryId: {
      type: String,
      default: '',
    },
    outOfDate: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    CollapseTransition: BaseUI.CollapseTransition,
    PredictedTransactionRow,
    Actions,
  },
  computed: {
    ...mapState('cashflowView', ['actionsExpandedFor']),
    canEditEnvelope() {
      return this.actions.length > 0 && this.canEditCategory;
    },
    hasActual() {
      return this.predictedTransaction.actual;
    },
    isIncome() {
      return this.predictedTransaction.isIncome;
    },
    isBalanced() {
      return this.predictedTransaction.balanceDatePassed;
    },
    incomeOrExpense() {
      return transactionUtils.incomeOrExpense(this.predictedTransaction);
    },
    isSelected() {
      return this.actionsExpandedFor === `${this.predictedTransaction.envelopeId}${this.categoryName}${this.categoryId}`;
    },
    actionsMapping() {
      return {
        editCategory: {
          text: `להעביר את ה${this.incomeOrExpense} למקום אחר `,
          icon: 'edits-move',
          onClick: this.openEditCategory,
        },
        updatePrediction: {
          icon: this.isBalanced ? 'edits-trash' : 'edit-prediction',
          onClick: this.openEditPredictedTransaction(EditEmptyEnvelopeFlow),
          text: this.isBalanced ? 'מחיקת צפי' : 'עריכת צפי',
        },
        changeCategoryWithActual: {
          icon: 'edits-sub-category',
          onClick: this.openEditPredictedTransaction(EditPredictedWithActualFlow),
          text: `לשנות את סוג ה${this.incomeOrExpense}`,
        },
        changeCategoryWOActual: {
          icon: 'edits-sub-category',
          onClick: this.openEditPredictedTransaction(EditPredictedWOActualFlow),
          text: `לשנות את סוג ה${this.incomeOrExpense}`,
        },
      };
    },
    actions() {
      return actionsPredicates(this.actionsMapping)
        .flatMap(predicate => predicate(this.hasActual, this.isIncome));
    },
  },
  methods: {
    ...mapActions('modalRootStore', ['openModal']),
    ...mapActions('editTransaction', ['startEditingTransaction']),
    ...mapActions('cashflowView', ['expandActionsOf']),
    openEditPredictedTransaction(popupToOpen) {
      return () => {
        Segment.trackUserInteraction('EditPredictedTransactionClicked', { businessName: this.predictedTransaction.name });
        this.openModal({
          component: popupToOpen,
          props: {
            predictedTransaction: this.predictedTransaction,
          },
        });
      };
    },
    openEditCategory() {
      Segment.trackUserInteraction('EditTransactionClicked', { businessName: this.predictedTransaction.businessName });
      this.startEditingTransaction({
        transaction: this.predictedTransaction.actual,
        categoryName: this.categoryName,
        categoryId: this.categoryId,
      });
      this.openModal({
        component: ChangeTransactionCategoryPopup,
        props: {},
      });
    },
    toggleActions() {
      if (this.isSelected) {
        this.expandActionsOf(null);
      } else {
        this.expandActionsOf(`${this.predictedTransaction.envelopeId}${this.categoryName}${this.categoryId}`);
      }
    },
  },
};

const actionsPredicates = actionsMapping => [
  (hasActual, isIncome) => (hasActual ? actionsMapping.editCategory : []),
  (hasActual, isIncome) => (hasActual ? actionsMapping.changeCategoryWithActual : []),
  (hasActual, isIncome) => (!hasActual ? actionsMapping.changeCategoryWOActual : []),
  (hasActual, isIncome) => (!hasActual ? actionsMapping.updatePrediction : []),
];

</script>

<style scoped lang="scss">
    @import '../../../../../../../../../../node_modules/base-ui/src/scss/riseup-colors';

  .predicted-transaction-container {
    transition: background-color 0.3s;
    background-color: $riseup_gray_0;
    &.selected {
      background-color: $riseup_gray_-1;
    }
    border-bottom: 1px solid $riseup_gray_0;
    &:first-of-type {
      box-shadow: inset 0 2px 3px 0 rgba(155, 155, 155, 0.51);
    }
    &:last-of-type {
      border-bottom: none;
    }
  }
</style>
